import type { Maybe, Place, PlaceHours, PlaceHoursGroup, Occurrence } from "../types";
import { isSameDate } from "../support/dates";
import { groupBy, first } from "lodash-es";
import { getClosed } from "./status";
import { reify, getScheduleGroups } from "./hours";

export function isScheduled(place: Place, placeHoursGroups: PlaceHoursGroup[], now: Date): boolean {
  if (getClosed(place, now)) {
    return false;
  }

  const placeHoursGroup = placeHoursGroups.find((p) => p.id == place.placeHoursGroupId);

  if (placeHoursGroup) {
    return placeHoursGroup.placeHours.length > 0;
  }

  return false;
}

export function getNextClosing(
  schedule: Array<Occurrence>,
  now: Date,
  timeZone: string
): Date | null {
  const hours = schedule.find((hours) => {
    const time = hours.until;

    return isSameDate(time, now, timeZone) && time >= now;
  });

  return hours ? hours.until : null;
}

export function getNextOpening(
  schedule: Array<Occurrence>,
  now: Date,
  timeZone: string
): Date | null {
  const hours = schedule.find((hours) => {
    const time = hours.from;

    return isSameDate(time, now, timeZone) && time >= now;
  });

  return hours ? hours.from : null;
}

type PlaceHoursGroupType = {
  type: string;
  hours: Maybe<Occurrence>;
};

const UNKNOWN_HOURS_GROUP = {
  // Probably a bug. Perhaps we should warn about this.
  type: "unknown",
  hours: null
};

const CLOSED_HOURS_GROUP = {
  // Place is scheduled, but does not have any hours rules for this day
  type: "closed",
  hours: null
};

export function getPlaceHoursGroupTypeType(
  hours: Array<Occurrence>,
  now: Date,
  timeZone: string
): PlaceHoursGroupType {
  const groups = getScheduleGroups(hours, now, timeZone);

  const order = ["closingSoon", "openingSoon", "open", "opening", "closed"];
  for (const type of order) {
    const hours = first(groups[type]);

    if (hours) {
      return { type, hours };
    }
  }

  return CLOSED_HOURS_GROUP;
}

export function getPlaceScheduleGroups(
  places: Place[],
  placeHoursGroups: PlaceHoursGroup[],
  now: Date,
  timeZone: string
): { [type: string]: PlaceHoursGroupType[] } {
  const items = places
    .filter((place) => isScheduled(place, placeHoursGroups, now))
    .map((place) => {
      const placeHoursGroup = placeHoursGroups.find((phg) => phg.id == place.placeHoursGroupId);

      if (placeHoursGroup == null) {
        return UNKNOWN_HOURS_GROUP;
      }

      const schedule = placeHoursGroup.placeHours.flatMap((hours: PlaceHours) => {
        return reify(hours, now, 0, timeZone);
      });

      const groupType = getPlaceHoursGroupTypeType(schedule, now, timeZone);

      return Object.assign({ place }, groupType);
    });

  return groupBy(items, "type");
}
